<template>
	<div ref="itemRef" :class="cls" @click="handleClick">
		<!-- <div v-if="showTail" :class="`${prefixCls}-tail`" /> -->
		<w-progress
			v-if="showTail"
			:class="`${prefixCls}-tail`"
			:percent="progressValue / 100"
			:stroke-color="'rgb(var(--primary-6))'"
			:size="'medium'"
			:show-text="false"
		/>
		<div v-if="type !== 'arrow'" :class="`${prefixCls}-node`">
			<slot name="node" :step="stepNumber" :status="computedStatus">
				<div v-if="type !== 'dot'" :class="iconCls">
					<slot name="icon" :step="stepNumber" :status="computedStatus">
						<icon-check v-if="computedStatus === 'finish'" />
						<icon-close v-else-if="computedStatus === 'error'" />
						<icon-loading v-else-if="computedStatus === 'process' || (stepsCtx?.current === 0 && index1 === 1)" />
						<icon-more v-else />
						<!-- <template v-else>{{ stepNumber }}</template> -->
					</slot>
				</div>
			</slot>
		</div>
		<div :class="`${prefixCls}-content`">
			<div :class="`${prefixCls}-title`">
				<slot>{{ title }}</slot>
			</div>
			<div v-if="description || $slots.description" :class="`${prefixCls}-description`">
				<slot name="description">
					{{ description }}
				</slot>
			</div>
		</div>
	</div>
</template>
  
  <script lang="ts">
import { computed, defineComponent, getCurrentInstance, inject, onBeforeUnmount, PropType, reactive, ref, watch } from 'vue';
import { getPrefixCls } from 'winbox-ui-next/es/_utils/global-config.js';
import IconCheck from 'winbox-ui-next/es/icon/icon-check';
import IconClose from 'winbox-ui-next/es/icon/icon-close';
import IconLoading from 'winbox-ui-next/es/icon/icon-loading';
import IconMore from 'winbox-ui-next/es/icon/icon-more';
import { StepStatus, StepsType } from './interface';
import { Direction } from 'winbox-ui-next/es/_utils/constant.js';
import { stepsInjectionKey } from './context';
import { useIndex } from 'winbox-ui-next/es/_hooks/use-index.js';

export default defineComponent({
	name: 'Step',
	components: {
		IconCheck,
		IconClose,
		IconLoading,
		IconMore,
	},
	props: {
		index: {
			type: Number,
			default: 0,
		},
		/**
		 * @zh 步骤的标题
		 * @en Title of the step
		 */
		title: String,
		/**
		 * @zh 步骤的描述信息
		 * @en Description of the step
		 */
		description: String,
		/**
		 * @zh 步骤的状态
		 * @en Status of the step
		 * @values 'wait', 'process', 'finish', 'error'
		 */
		status: {
			type: String as PropType<StepStatus>,
		},
		/**
		 * @zh 是否禁用
		 * @en Whether to disable
		 */
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	/**
	 * @zh 节点
	 * @en Node
	 * @slot node
	 * @binding {number} step
	 * @binding {string} status
	 */
	/**
	 * @zh 图标
	 * @en Icon
	 * @slot icon
	 * @binding {number} step
	 * @binding {string} status
	 */
	/**
	 * @zh 描述内容
	 * @en Description
	 * @slot description
	 */
	setup(props) {
		const prefixCls = getPrefixCls('steps-item');
		const instance = getCurrentInstance();
		const iconCls = getPrefixCls('steps-icon');

		const stepsCtx = inject(stepsInjectionKey, undefined);

		const type = computed(() => stepsCtx?.type ?? 'default');

		const itemRef = ref<HTMLElement>();
		const { computedIndex } = useIndex({
			itemRef,
			selector: `.${prefixCls}`,
			parentClassName: stepsCtx?.parentCls,
		});
		const stepNumber = computed(() => computedIndex.value + 1);

		const computedStatus = computed(() => props.status ?? stepsCtx?.getStatus(stepNumber.value) ?? 'process');

		const nextStepError = computed(() => stepsCtx?.errorSteps.includes(stepNumber.value + 1) ?? false);

		const progress = ref(0); // 动态进度条，从0%开始
		const stepCounter = ref(0); // 进度步骤计数
		const progressTimer = ref(0);
		const progressValue = ref(0);
		const setProgress = () => {
			// 每 50 毫秒更新一次进度条
			
			
			progressTimer.value = setInterval(function () {
				var maxProgress = 95; // 根据条件决定最大进度
				// 如果进度达到或超过最大值，停止定时器
				if (progressValue.value >= maxProgress) {
					progressValue.value = 100;
					clearInterval(progressTimer.value); // 停止进度定时器
					progressTimer.value = 0; // 清空定时器引用
					return 
				}

				// 每次递增步骤计数
				stepCounter.value += 1;

				// 如果步骤计数 <= 10，表示第一阶段进度
				if (stepCounter.value <= 10) {
					progressValue.value = stepCounter.value / 100;
				} else {
					// 第二阶段进度，每次根据当前进度递增
					progressValue.value += progressValue.value < maxProgress - 50 ? 0.7 : (maxProgress - progressValue.value) / 70;
				}

				
			}, 50);
		};


		watch([() => stepsCtx?.current, () => stepNumber.value], (val) => {
			
			if (stepNumber.value === stepsCtx?.current) {
					setProgress();
				
			} else if (stepNumber.value < stepsCtx?.current) {
				progressValue.value = 100;
			} else {
				progressValue.value = 0;
			}
		});

		if (instance) {
			stepsCtx?.addItem(
				instance.uid,
				reactive({
					step: stepNumber,
					status: computedStatus,
				})
			);
		}

		onBeforeUnmount(() => {
			if (instance) {
				stepsCtx?.removeItem(instance.uid);
			}
		});

		const showTail = computed(() => !stepsCtx?.lineLess && (stepsCtx?.labelPlacement === 'vertical' || stepsCtx?.direction === 'vertical'));

		const handleClick = (ev: Event) => {
			if (!props.disabled) {
				stepsCtx?.onClick(stepNumber.value, ev);
			}
		};

		const cls = computed(() => [
			prefixCls,
			`${prefixCls}-${computedStatus.value}`,
			{
				[`${prefixCls}-active`]: stepNumber.value === stepsCtx?.current,
				[`${prefixCls}-next-error`]: nextStepError.value,
				[`${prefixCls}-disabled`]: props.disabled,
				// [`${prefixCls}-custom`]: !!icon,
			},
		]);

		return {
			prefixCls,
			iconCls,
			cls,
			itemRef,
			showTail,
			stepNumber,
			computedStatus,
			type,
			handleClick,
			progressValue,
			progressTimer,
			stepsCtx,
			index1:props.index
		};
	},
});
</script>
<style lang="scss" scoped>
.w-steps-item-finish .w-steps-icon {
	color: var(--color-white);
	background-color: rgb(var(--primary-6));
	border: 1px solid transparent;
}
.w-steps-label-vertical {
	.w-steps-item-title{
		font-size: 14px;
		color: #646479 !important;
	}
	.w-steps-icon {
		width: 16px;
		height: 16px;
		line-height: 13px;
		font-size: 12px;
	}
	.w-steps-item {
		&:last-child {
			text-align: right;
			.w-steps-item-content {
				position: absolute;
				right: 0;
			}
		}
		&:first-child{
			.w-steps-icon {
			color: var(--color-white) !important;
			background-color: rgb(var(--primary-6)) !important;
			border: 1px solid transparent !important;
			}
		}
		&:not(:last-child) {
			.w-steps-item-tail {
				display: block;
				top: 7.5px;
			}
		}
	}
	.w-steps-item-tail {
		display: none;
		left: 18px;
		padding-right: 8px;
		height: 0;
		:deep(.w-progress-line) {
		}
	}
	.w-steps-item-node {
		margin: 0px;
	}
	.w-steps-item-content {
		width: auto;
		text-align: left;
	}
}
</style>